/**
 * User Preferences Utilities for JOYOUS Admin Dashboard
 * Comprehensive utilities for managing user preferences with validation and migration
 */

import {
  UserPreferences,
  UserPreferencesState,
  DEFAULT_PREFERENCES,
  PREFERENCES_VALIDATION,
  PreferenceUpdatePayload,
  PreferencesStorageConfig,
  DEFAULT_STORAGE_CONFIG,
  PreferenceMigration,
  PreferenceChangeEvent
} from '@/types/user-preferences';

/**
 * Validates a single preference value
 */
export function validatePreference<K extends keyof UserPreferences>(
  key: K,
  value: UserPreferences[K]
): { isValid: boolean; error?: string } {
  const validationRules = PREFERENCES_VALIDATION[key as keyof typeof PREFERENCES_VALIDATION];

  if (validationRules && Array.isArray(validationRules)) {
    if (!validationRules.includes(value as any)) {
      return {
        isValid: false,
        error: `Invalid ${key} value: ${value}. Expected one of: ${validationRules.join(', ')}`
      };
    }
  }

  // Additional custom validations
  switch (key) {
    case 'timezone':
      try {
        Intl.DateTimeFormat(undefined, { timeZone: value });
        return { isValid: true };
      } catch {
        return { isValid: false, error: 'Invalid timezone' };
      }
    case 'currency':
      if (typeof value !== 'string' || value.length !== 3) {
        return { isValid: false, error: 'Invalid currency code format' };
      }
      break;
  }

  return { isValid: true };
}

/**
 * Validates complete preferences object
 */
export function validatePreferences(preferences: Partial<UserPreferences>): {
  isValid: boolean;
  errors: string[];
  validPreferences: Partial<UserPreferences>;
} {
  const errors: string[] = [];
  const validPreferences: Partial<UserPreferences> = {};

  for (const [key, value] of Object.entries(preferences)) {
    const validation = validatePreference(key as keyof UserPreferences, value as any);
    if (validation.isValid) {
      (validPreferences as any)[key] = value;
    } else {
      errors.push(validation.error || `Invalid ${key}`);
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
    validPreferences
  };
}

/**
 * Merges user preferences with defaults
 */
export function mergeWithDefaults(userPreferences: Partial<UserPreferences>): UserPreferences {
  return {
    ...DEFAULT_PREFERENCES,
    ...userPreferences
  };
}

/**
 * Creates a deep copy of preferences
 */
export function clonePreferences(preferences: UserPreferences): UserPreferences {
  return JSON.parse(JSON.stringify(preferences));
}

/**
 * Checks if preferences have changed
 */
export function havePreferencesChanged(
  oldPreferences: UserPreferences,
  newPreferences: Partial<UserPreferences>
): boolean {
  for (const [key, value] of Object.entries(newPreferences)) {
    if (oldPreferences[key as keyof UserPreferences] !== value) {
      return true;
    }
  }
  return false;
}

/**
 * Generates preference change events
 */
export function generatePreferenceChangeEvents(
  oldPreferences: UserPreferences,
  newPreferences: UserPreferences
): PreferenceChangeEvent[] {
  const events: PreferenceChangeEvent[] = [];

  for (const [key, newValue] of Object.entries(newPreferences)) {
    const oldValue = oldPreferences[key as keyof UserPreferences];
    if (oldValue !== newValue) {
      events.push({
        key: key as keyof UserPreferences,
        oldValue,
        newValue,
        timestamp: new Date()
      });
    }
  }

  return events;
}

/**
 * Local storage utilities
 */
export class PreferencesStorage {
  private config: PreferencesStorageConfig;

  constructor(config: Partial<PreferencesStorageConfig> = {}) {
    this.config = { ...DEFAULT_STORAGE_CONFIG, ...config };
  }

  /**
   * Save preferences to storage
   */
  save(preferences: UserPreferences): void {
    try {
      const storage = this.config.storageType === 'sessionStorage'
        ? sessionStorage
        : localStorage;

      const data = {
        preferences,
        version: 1,
        timestamp: new Date().toISOString()
      };

      if (this.config.encryptionEnabled) {
        // Basic encryption (in production, use proper encryption)
        const encrypted = btoa(JSON.stringify(data));
        storage.setItem(this.config.storageKey, encrypted);
      } else {
        storage.setItem(this.config.storageKey, JSON.stringify(data));
      }
    } catch (error) {
      console.error('Failed to save preferences:', error);
      throw new Error('Failed to save preferences to storage');
    }
  }

  /**
   * Load preferences from storage
   */
  load(): UserPreferences | null {
    try {
      const storage = this.config.storageType === 'sessionStorage'
        ? sessionStorage
        : localStorage;

      const stored = storage.getItem(this.config.storageKey);
      if (!stored) return null;

      let data: any;
      if (this.config.encryptionEnabled) {
        data = JSON.parse(atob(stored));
      } else {
        data = JSON.parse(stored);
      }

      // Apply migrations based on version
      const migratedPreferences = this.applyMigrations(data.version, data.preferences);

      // Validate loaded preferences
      const validation = validatePreferences(migratedPreferences);
      if (!validation.isValid) {
        console.warn('Invalid preferences in storage, using defaults:', validation.errors);
        return null;
      }

      return mergeWithDefaults(validation.validPreferences);
    } catch (error) {
      console.error('Failed to load preferences:', error);
      return null;
    }
  }

  /**
   * Remove preferences from storage
   */
  clear(): void {
    try {
      const storage = this.config.storageType === 'sessionStorage'
        ? sessionStorage
        : localStorage;
      storage.removeItem(this.config.storageKey);
    } catch (error) {
      console.error('Failed to clear preferences:', error);
    }
  }

  /**
   * Check if preferences exist in storage
   */
  exists(): boolean {
    try {
      const storage = this.config.storageType === 'sessionStorage'
        ? sessionStorage
        : localStorage;
      return storage.getItem(this.config.storageKey) !== null;
    } catch {
      return false;
    }
  }

  /**
   * Apply migrations to old preference versions
   */
  private applyMigrations(version: number, preferences: any): UserPreferences {
    const migrations: PreferenceMigration[] = [
      // Add future migrations here
      {
        version: 1,
        description: 'Initial version',
        migrate: (prefs) => mergeWithDefaults(prefs)
      }
    ];

    let migrated = preferences;

    // Apply migrations from stored version to latest
    for (const migration of migrations) {
      if (migration.version > version) {
        try {
          migrated = migration.migrate(migrated);
        } catch (error) {
          console.error(`Failed to apply migration v${migration.version}:`, error);
          break;
        }
      }
    }

    return migrated;
  }
}

/**
 * Preference formatting utilities
 */
export class PreferenceFormatter {
  constructor(private preferences: UserPreferences) {}

  /**
   * Get locale string based on preferences
   */
  getLocale(): string {
    return this.preferences.language === 'ar' ? 'ar-SA' : 'en-US';
  }

  /**
   * Check if Arabic interface is preferred
   */
  isArabicInterface(): boolean {
    return this.preferences.language === 'ar';
  }

  /**
   * Check if Arabic numerals should be used
   */
  shouldUseArabicNumerals(): boolean {
    return this.preferences.numberSystem === 'arabic';
  }

  /**
   * Check if Hijri calendar should be used
   */
  shouldUseHijriCalendar(): boolean {
    return this.preferences.dateFormat === 'hijri';
  }

  /**
   * Check if mixed calendar should be used
   */
  shouldUseMixedCalendar(): boolean {
    return this.preferences.dateFormat === 'mixed';
  }

  /**
   * Get formatting options for numbers
   */
  getNumberFormattingOptions() {
    return {
      locale: this.getLocale(),
      useArabicNumerals: this.shouldUseArabicNumerals(),
      currency: this.preferences.currency
    };
  }

  /**
   * Get formatting options for dates
   */
  getDateFormattingOptions() {
    return {
      locale: this.getLocale(),
      useHijri: this.shouldUseHijriCalendar(),
      useMixed: this.shouldUseMixedCalendar(),
      timezone: this.preferences.timezone
    };
  }
}

/**
 * Create preference formatter instance
 */
export function createPreferenceFormatter(preferences: UserPreferences): PreferenceFormatter {
  return new PreferenceFormatter(preferences);
}

/**
 * Preference comparison utilities
 */
export function isEqualPreference(
  pref1: UserPreferences,
  pref2: UserPreferences
): boolean {
  return JSON.stringify(pref1) === JSON.stringify(pref2);
}

/**
 * Get preference summary for debugging
 */
export function getPreferenceSummary(preferences: UserPreferences): string {
  return `
    Language: ${preferences.language}
    Date Format: ${preferences.dateFormat}
    Number System: ${preferences.numberSystem}
    Timezone: ${preferences.timezone}
    Currency: ${preferences.currency}
    Theme: ${preferences.theme}
  `.trim();
}

/**
 * Export preference configuration
 */
export function exportPreferences(preferences: UserPreferences): string {
  return JSON.stringify({
    ...preferences,
    exportedAt: new Date().toISOString(),
    version: '1.0'
  }, null, 2);
}

/**
 * Import preference configuration
 */
export function importPreferences(jsonString: string): {
  success: boolean;
  preferences?: UserPreferences;
  error?: string;
} {
  try {
    const imported = JSON.parse(jsonString);
    const validation = validatePreferences(imported);

    if (validation.isValid) {
      return {
        success: true,
        preferences: mergeWithDefaults(validation.validPreferences)
      };
    } else {
      return {
        success: false,
        error: validation.errors.join(', ')
      };
    }
  } catch (error) {
    return {
      success: false,
      error: 'Invalid JSON format'
    };
  }
}

export default {
  validatePreference,
  validatePreferences,
  mergeWithDefaults,
  clonePreferences,
  havePreferencesChanged,
  generatePreferenceChangeEvents,
  PreferencesStorage,
  PreferenceFormatter,
  createPreferenceFormatter,
  isEqualPreference,
  getPreferenceSummary,
  exportPreferences,
  importPreferences
};